'use client';

/**
 * ConsumerBrowsePanel Component
 *
 * Browse-and-link panel that lets consumers link their previously uploaded
 * documents from other cases to the current case.
 *
 * Issue #1145: Data Room — Consumer Portal document reuse
 */

import FileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  GlassButton,
  GlassChip,
  ShimmerLoader,
  SplitPanelContent,
  SplitPanelHeader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { DOCUMENT_TYPE_DISPLAY_NAMES, type DocumentType } from '@ops-matrix/shared-types';
import { useMemo } from 'react';
import { trpc } from '../../lib/trpc/client';

interface ConsumerBrowsePanelProps {
  caseId: string;
  onClose: () => void;
  onLink: (documentId: string, consumerId: string) => void;
  isLinking?: boolean;
}

export function ConsumerBrowsePanel({ caseId, onClose, onLink, isLinking = false }: ConsumerBrowsePanelProps) {
  const { t } = useI18n();

  const { data, isLoading } = trpc.dataRoom.myReusableDocuments.useQuery({ caseId }, { enabled: !!caseId });

  const documents = useMemo(() => data?.documents ?? [], [data?.documents]);
  const consumerId = data && 'consumerId' in data ? data.consumerId : undefined;

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case 'VERIFIED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
      case 'EXPIRED':
        return 'error';
      case 'NEEDS_REUPLOAD':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <>
      <SplitPanelHeader title={t('dataRoom.browseMyDocuments') ?? 'My Documents'} onClose={onClose} />
      <SplitPanelContent>
        {isLoading ? (
          <ShimmerLoader lines={5} />
        ) : documents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              {t('dataRoom.noReusableDocuments') ?? 'No documents available to link'}
            </Typography>
            <Typography variant="body2" color="textDisabled" sx={{ mt: 1 }}>
              {t('dataRoom.noReusableDocumentsHint') ??
                'Documents you upload to other applications will appear here for reuse'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              {t('dataRoom.browseMyDocumentsDescription', { count: documents.length }) ??
                `${documents.length} document(s) from your other applications available to link`}
            </Typography>
            {documents.map((doc) => (
              <Stack
                key={doc.documentId}
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FileIcon fontSize="small" color="primary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {doc.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {doc.documentType
                      ? (DOCUMENT_TYPE_DISPLAY_NAMES[doc.documentType as DocumentType] ?? doc.documentType)
                      : (t('dataRoom.unknownType') ?? 'Unknown type')}
                  </Typography>
                </Box>
                {doc.verificationStatus && (
                  <GlassChip
                    label={t(`status.${doc.verificationStatus.toLowerCase()}`)}
                    size="small"
                    color={getStatusColor(doc.verificationStatus) as 'success' | 'warning' | 'error' | 'default'}
                  />
                )}
                <GlassButton
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => consumerId && onLink(doc.documentId, consumerId)}
                  disabled={isLinking || !consumerId}
                >
                  {t('dataRoom.link') ?? 'Link'}
                </GlassButton>
              </Stack>
            ))}
          </Stack>
        )}
      </SplitPanelContent>
    </>
  );
}
